import { Dashboard } from "api/model/dashboard/Dashboard";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import React, { useState } from "react";
import { T } from "services/lang/LanguageService";

interface DatasetSearchDialogProps {
  dashboard: Dashboard;
  isDatasetSearchDialogVisible: boolean;
  setIsDatasetSearchDialogVisible: (isDatasetSearchDialogVisible: boolean) => void;
}

interface DatasetLocationParams {
  tabId: number;
  groupId: string;
}

const DatasetSearchDialog = (props: DatasetSearchDialogProps) => {
  const [selectedDataset, setSelectedDataset] = useState<string | undefined>();
  const [foundLocations, setFoundLocations] = useState<DatasetLocationParams[]>([]);

  const findDatasetInDashboard = (identifier: string) => {
    if (!identifier) return;
    const currentFoundLocations: DatasetLocationParams[] = [];

    const searchDataset = (obj: any): boolean => {
      if (typeof obj === "object" && obj !== null) {
        return Object.values(obj).some(value => {
          if (typeof value === "object") {
            return searchDataset(value);
          }
          return value === identifier;
        });
      }
      return false;
    };

    props.dashboard.dashboardTabsJs.forEach((tab, tabId) => {
      tab.componentGroups.forEach(group => {
        group.components.forEach(component => {
          if (searchDataset(component)) {
            currentFoundLocations.push({ tabId, groupId: group.id });
          }
        });
      });
    });

    setFoundLocations(currentFoundLocations);
  };

  const openTabWithLocationInNewBrowserTab = (tabId: number) => {
    window.open(
      `${window.location.pathname}#/dashboard/${props.dashboard?.id}/tab/${tabId}`,
      "_blank"
    );
  };

  const tabNameTemplate = (rowData: DatasetLocationParams) => {
    return props.dashboard.dashboardTabsJs[rowData.tabId]?.title || T("word.unknown-title");
  };

  const groupNameTemplate = (rowData: DatasetLocationParams) => {
    const group = props.dashboard.dashboardTabsJs[rowData.tabId]?.componentGroups.find(
      currentGroup => currentGroup.id === rowData.groupId
    );
    return group?.title || T("word.unknown-title");
  };

  const actionButtonTemplate = (rowData: DatasetLocationParams) => {
    return (
      <Button
        icon="pi pi-external-link"
        onClick={() => openTabWithLocationInNewBrowserTab(rowData.tabId)}
        className="p-button-sm p-button-text"
      />
    );
  };

  return (
    <Dialog
      header={T("action.search-dataset")}
      visible={props.isDatasetSearchDialogVisible}
      onHide={() => props.setIsDatasetSearchDialogVisible(false)}
      className="w-4"
      draggable
    >
      <div className="flex flex-column gap-5">
        <div className="flex flex-column gap-1">
          <label htmlFor="identifier">{T("word.identifier")}</label>
          <DatasetDropdown
            value={selectedDataset}
            onChange={e => {
              setSelectedDataset(e || "");
              findDatasetInDashboard(e || "");
            }}
          />
        </div>
        <DataTable value={foundLocations} paginator rows={10}>
          <Column
            field="tabId"
            header={T("word.tab")}
            style={{ width: "45%" }}
            body={tabNameTemplate}
          />
          <Column
            field="groupId"
            header={T("word.group")}
            style={{ width: "40%" }}
            body={groupNameTemplate}
          />
          <Column
            header={T("action.open-tab")}
            style={{ width: "15%" }}
            body={actionButtonTemplate}
          />
        </DataTable>
      </div>
    </Dialog>
  );
};

export default DatasetSearchDialog;
